// src/features/roles/mutations.ts
import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  createRole,
  updateRole,
  deleteRole,
  updateRoleGrants,
} from "./api";

import { RolesKeys } from "./types";
import type { Role, RoleDTO } from "./types";

/* =========================
 * ROLES — Write Hooks (Mutations)
 * ========================= */

/** 🔹 Create role */
export function useCreateRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: RoleDTO.Create) => createRole(payload),
    onSuccess: (created) => {
      // Prime detail + refresh lists
      qc.setQueryData(RolesKeys.detail(created.id), created);
      qc.invalidateQueries({ queryKey: RolesKeys.all });
    },
  });
}

/** 🔹 Update role (optimistic detail + list patch)
 *  Note: only name/code/description are updated, no status/audits.
 */
export function useUpdateRole(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: RoleDTO.Update) => updateRole(id, payload),
    onMutate: async (payload) => {
      await Promise.all([
        qc.cancelQueries({ queryKey: RolesKeys.detail(id) }),
        qc.cancelQueries({ queryKey: RolesKeys.all }),
      ]);

      // Snapshot current caches
      const prevDetail = qc.getQueryData<Role>(RolesKeys.detail(id));
      const prevLists = qc
        .getQueryCache()
        .findAll({ queryKey: RolesKeys.all })
        .map((q) => ({
          key: q.queryKey,
          data: q.state.data as RoleDTO.Page<Role> | undefined,
        }));

      // Optimistically update detail
      if (prevDetail) {
        qc.setQueryData<Role>(RolesKeys.detail(id), { ...prevDetail, ...payload });
      }

      // Optimistically patch any cached lists
      for (const snap of prevLists) {
        const data = snap.data;
        if (!data?.items) continue;
        const items = data.items.map((r) =>
          r.id === id ? ({ ...r, ...payload } as Role) : r
        );
        qc.setQueryData(snap.key, { ...data, items });
      }

      return { prevDetail, prevLists };
    },
    onError: (_err, _payload, ctx) => {
      if (!ctx) return;
      if (ctx.prevDetail) qc.setQueryData(RolesKeys.detail(id), ctx.prevDetail);
      for (const snap of ctx.prevLists ?? []) {
        qc.setQueryData(snap.key, snap.data);
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: RolesKeys.detail(id) });
      qc.invalidateQueries({ queryKey: RolesKeys.all });
    },
  });
}

/** 🔹 Delete role (optimistic remove from lists)
 *  Once deleted, it's gone — no soft-delete handling on the UI.
 */
export function useDeleteRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteRole(id),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: RolesKeys.all });

      const prevLists = qc
        .getQueryCache()
        .findAll({ queryKey: RolesKeys.all })
        .map((q) => ({
          key: q.queryKey,
          data: q.state.data as RoleDTO.Page<Role> | undefined,
        }));

      // Remove from lists
      for (const snap of prevLists) {
        const data = snap.data;
        if (!data?.items) continue;
        qc.setQueryData(snap.key, {
          ...data,
          items: data.items.filter((r) => r.id !== id),
          total: Math.max(0, (data.total ?? 1) - 1),
        });
      }

      // Drop detail cache
      qc.removeQueries({ queryKey: RolesKeys.detail(id) });

      return { prevLists };
    },
    onError: (_err, _id, ctx) => {
      for (const snap of ctx?.prevLists ?? []) {
        qc.setQueryData(snap.key, snap.data);
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: RolesKeys.all });
    },
  });
}

/** 🔹 Update role grants (permissions) */
export function useUpdateRoleGrants(roleId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: RoleDTO.UpdateGrantsPayload) =>
      updateRoleGrants(roleId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: RolesKeys.grants(roleId) });
      qc.invalidateQueries({ queryKey: RolesKeys.all });
    },
  });
}
